import { Box, CircularProgress, Typography, Grid, useMediaQuery } from '@mui/material'
import s from './ExistingClient.modules.scss'
import { Button, Input } from '@fullscreendigital/carrefour-dex-fe'
import { Table } from 'components/core-ui/Table'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { ChangeEvent, useEffect, useState } from 'react'
import { makeCoreGetRequest } from 'utils/api'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { columns, toTableRowItems } from './helpers'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CreateClientModal } from './CreateClientModal'
import { TableRowItem } from './interfaces'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import { ViewActionIcon } from 'icons'

interface ExistingClientProps {
  selectNewClient?: boolean
}

const ExistingClient: React.FC<ExistingClientProps> = ({ selectNewClient }) => {
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [searchField, setSearchField] = useState<string>('')
  const [showTable, setShowTable] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [tableItems, setTableItems] = useState<TableRowItem[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [isCreateClientModalOpen, setIsCreateClientModalOpen] = useState<boolean>(false)

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/supply-omni/client',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy,
          searchFields: ['name', 'cif', 'phone'],
          searchText: searchField
        })
      )
      setTableItems(toTableRowItems(response.list))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)

      if (searchField && response.list.length === 0) {
        setIsCreateClientModalOpen(true)
      }
    } catch (error) {
      showNotification({
        message: `Error while fetching table data. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const openCreateClientTab = (): void => {
    setSearchParams(
      prevSearchParams => {
        const updatedSearchParams = new URLSearchParams(prevSearchParams)
        updatedSearchParams.set('tab', '1')
        return updatedSearchParams
      },
      { replace: true }
    )
  }

  const isXs = useMediaQuery('(max-width:624px)')

  return (
    <>
      {isLoading && (
        <div style={{ display: 'flex' }}>
          <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
        </div>
      )}

      {isCreateClientModalOpen && (
        <CreateClientModal
          isOpen={isCreateClientModalOpen}
          closeModal={() => setIsCreateClientModalOpen(false)}
          onSubmit={openCreateClientTab}
        />
      )}

      <Box className={s.searchPaper}>
        {/* <Box className={s.searchContainer}> */}
        <Grid container spacing={4}>
          <Grid item xs={12} sm={8} md={9}>
            <Input
              type="text"
              name="name"
              label="CUI / Nume / Telefon"
              variant="outlined"
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchField(e.target.value)}
              value={searchField}
              fullWidth
              style={{ justifyContent: 'center' }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Button
              onClick={() => {
                if (searchField) {
                  // to avoid initiallly making 2 calls
                  showTable && setShouldRefreshData(true)
                  setShowTable(true)
                }
              }}
              label=""
              variant="contained"
              style={isXs ? {} : { marginTop: '20px' }}
            >
              <SearchOutlinedIcon /> CAUTA CLIENT
            </Button>
          </Grid>
        </Grid>
        {/* </Box> */}
      </Box>

      {searchField && showTable ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '12px'
          }}
        >
          <Table
            hideCheckbox
            orderBy="name"
            order="asc"
            columns={columns}
            items={tableItems}
            fetchData={fetchData}
            isLoading={isLoading}
            totalCount={totalCount}
            shouldRefreshData={shouldRefreshData}
            setShouldRefreshData={setShouldRefreshData}
            rowActions={[
              {
                label: 'Selecteaza',
                labelColor: 'white',
                typographyVariant: 'medium',
                backgroundColor: '#0970E6',
                desktopIcon: <ViewActionIcon />,
                onClick: (e, rowId) =>
                  selectNewClient
                    ? navigate(
                        `/supplyOmni/receiptClientDetails/${rowId}?orderId=${searchParams.get(
                          'orderId'
                        )}`
                      )
                    : navigate(`/supplyOmni/shoppingCartClientDetails/${rowId}`)
              }
            ]}
          />
        </div>
      ) : (
        <Grid sx={{ mt: 4 }} container justifyContent="center" spacing={2}>
          <Box sx={{ textAlign: 'center' }}>
            <PersonSearchIcon sx={{ fontSize: '100px', color: '#cecece' }} />
            <br />
            <Typography sx={{ fontSize: '14px', color: '#cecece' }}>Cauta client</Typography>
            <br />
          </Box>
        </Grid>
      )}
    </>
  )
}

export default ExistingClient
